<template>
  <section>
    <div class="guests-page__head" style="margin: 4px 0 6px">
      <div>
        <h2 class="guests-page__title" style="font-size: 18px">
          Template documentali
        </h2>
        <p class="guests-page__subtitle">
          Contratti, regolamenti e informative di riferimento della struttura
        </p>
      </div>
      <button class="villa-btn villa-btn--primary" @click="openUpload">
        <i class="fa-solid fa-file-arrow-up"></i> Carica template
      </button>
    </div>

    <p class="tpl-info">
      <i class="fa-solid fa-circle-info"></i> I template caricati saranno usati
      nella fase successiva per generare automaticamente i contratti degli
      ospiti.
    </p>

    <div v-if="loading" class="guests-loading">Caricamento template…</div>
    <div v-else-if="error" class="guests-loading">{{ error }}</div>
    <div v-else-if="templates.length === 0" class="guests-loading">
      Non sono ancora presenti template documentali per questa struttura.
    </div>

    <div v-else class="guests-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Categoria</th>
            <th>File</th>
            <th>Formato</th>
            <th>Ver.</th>
            <th>Stato</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in templates" :key="t.id">
            <td>{{ t.name }}</td>
            <td>{{ categoryLabel(t.category) }}</td>
            <td class="doc-file">{{ t.originalFilename }}</td>
            <td>{{ formatLabel(t.originalFilename) }}</td>
            <td>v{{ t.version }}</td>
            <td>
              <span
                v-if="t.isActive"
                class="tpl-badge tpl-badge--active"
                title="Template attivo"
              >
                Attivo
              </span>
              <span v-else class="tpl-badge">—</span>
            </td>
            <td class="actions-cell">
              <button
                class="icon-btn"
                title="Scarica"
                :disabled="downloadingId === t.id"
                @click="download(t)"
              >
                <i class="fa-solid fa-download"></i>
              </button>
              <button
                v-if="!t.isActive"
                class="icon-btn"
                title="Imposta attivo"
                @click="setActive(t)"
              >
                <i class="fa-solid fa-circle-check"></i>
              </button>
              <button
                class="icon-btn icon-btn--danger"
                title="Archivia / disattiva"
                @click="askArchive(t)"
              >
                <i class="fa-solid fa-box-archive"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale upload -->
    <VueFinalModal v-model="uploadModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">Carica template documentale</h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Nome template</label>
          <input
            v-model.trim="uploadModal.name"
            type="text"
            placeholder="Opzionale: se vuoto si usa il nome del file"
          />
        </div>
        <div class="form-field form-field--full">
          <label>Categoria</label>
          <select v-model="uploadModal.category">
            <option v-for="c in categories" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>
        <div class="form-field form-field--full">
          <label>File (PDF / DOC / DOCX) <span class="required">*</span></label>
          <input
            ref="fileInput"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            @change="onFileChange"
          />
          <span v-if="uploadModal.fileName" class="doc-file">
            {{ uploadModal.fileName }}
          </span>
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="uploadModal.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="uploadModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="uploadModal.busy"
          @click="submitUpload"
        >
          {{ uploadModal.busy ? "Caricamento…" : "Carica" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma archiviazione -->
    <VueFinalModal v-model="archiveConfirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Archivia template</h3>
      <p class="villa-confirm__text">
        Vuoi archiviare <strong>{{ archiveConfirm.name }}</strong
        >? Non sarà più visibile in elenco.
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="archiveConfirm.busy"
          @click="archiveConfirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="archiveConfirm.busy"
          @click="confirmArchive"
        >
          {{ archiveConfirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXT = /\.(pdf|doc|docx)$/i;

const CATEGORIES = [
  { value: "contract", label: "Contratto" },
  { value: "privacy", label: "Privacy" },
  { value: "service_rules", label: "Regolamento" },
  { value: "service_card", label: "Carta servizi" },
  { value: "other", label: "Altro" },
];

export default {
  name: "FacilityDocumentTemplatesPanel",
  props: {
    facilityId: { type: [String, Number], required: true },
  },
  data() {
    return {
      templates: [],
      loading: true,
      error: "",
      downloadingId: null,
      categories: CATEGORIES,
      uploadModal: {
        open: false,
        busy: false,
        name: "",
        category: "contract",
        notes: "",
        file: null,
        fileName: "",
      },
      archiveConfirm: { open: false, busy: false, id: null, name: "" },
    };
  },
  methods: {
    ...mapActions([
      "fetchFacilityDocumentTemplates",
      "uploadFacilityDocumentTemplate",
      "updateFacilityDocumentTemplate",
      "archiveFacilityDocumentTemplate",
      "downloadFacilityDocumentTemplate",
    ]),
    categoryLabel(v) {
      const f = CATEGORIES.find((c) => c.value === v);
      return f ? f.label : v;
    },
    formatLabel(filename) {
      const m = /\.([^.]+)$/.exec(filename || "");
      return m ? m[1].toUpperCase() : "—";
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.templates = await this.fetchFacilityDocumentTemplates(
          this.facilityId
        );
      } catch (err) {
        this.error =
          err?.response?.data?.error || "Errore nel caricamento dei template.";
      } finally {
        this.loading = false;
      }
    },
    openUpload() {
      this.uploadModal = {
        open: true,
        busy: false,
        name: "",
        category: "contract",
        notes: "",
        file: null,
        fileName: "",
      };
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      this.uploadModal.file = file || null;
      this.uploadModal.fileName = file ? file.name : "";
    },
    validateFile(file) {
      if (!file) return "Seleziona un file.";
      if (!ALLOWED_EXT.test(file.name || ""))
        return "Formato non ammesso. Sono accettati solo .pdf, .doc, .docx.";
      if (file.size > MAX_SIZE) return "Il file supera il limite di 10MB.";
      return "";
    },
    async submitUpload() {
      const file = this.uploadModal.file;
      const validationError = this.validateFile(file);
      if (validationError) return this.$toast.error(validationError);
      this.uploadModal.busy = true;
      try {
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("category", this.uploadModal.category || "other");
        if (this.uploadModal.name)
          formData.append("name", this.uploadModal.name);
        if (this.uploadModal.notes)
          formData.append("notes", this.uploadModal.notes);
        await this.uploadFacilityDocumentTemplate({
          facilityId: this.facilityId,
          formData,
        });
        this.$toast.success("Template caricato.");
        this.uploadModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Caricamento non riuscito."
        );
      } finally {
        this.uploadModal.busy = false;
      }
    },
    async setActive(t) {
      try {
        await this.updateFacilityDocumentTemplate({
          facilityId: this.facilityId,
          templateId: t.id,
          payload: { isActive: true },
        });
        this.$toast.success("Template impostato come attivo.");
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Impossibile attivare il template."
        );
      }
    },
    async download(t) {
      this.downloadingId = t.id;
      let url = null;
      try {
        const response = await this.downloadFacilityDocumentTemplate({
          facilityId: this.facilityId,
          templateId: t.id,
        });
        const blob = new Blob([response.data], {
          type: response.data?.type || t.mimeType || "application/octet-stream",
        });
        url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = t.originalFilename || `template-${t.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Download non riuscito."
        );
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        this.downloadingId = null;
      }
    },
    askArchive(t) {
      this.archiveConfirm = {
        open: true,
        busy: false,
        id: t.id,
        name: t.name,
      };
    },
    async confirmArchive() {
      this.archiveConfirm.busy = true;
      try {
        await this.archiveFacilityDocumentTemplate({
          facilityId: this.facilityId,
          templateId: this.archiveConfirm.id,
        });
        this.$toast.success("Template archiviato.");
        this.archiveConfirm.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Archiviazione non riuscita."
        );
      } finally {
        this.archiveConfirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/GuestDocumentsPanel.css" />
